// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct } from "@insite/client-framework/Components/ProductContext";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Img, { ImgProps } from "@insite/mobius/Img";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import AnchorHeading, { AnchorHeadingStyles } from "@store/Library/Components/Toolkit/Anchor/AnchorHeading";
import Block, { BlockProps } from "@store/Library/Components/Toolkit/Block";
import LazyImage, { LazyImageProps } from "@store/MobiusOverrides/LazyImage/LazyImage";
import { linkAltStyles } from "@store/Themes/common/mixins/link";
import { ApplicationsCategoriesModel } from "@store/Framework/Types/ApiModels";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import * as React from "react";
import { useContext } from "react";
import { css } from "styled-components";

const enum fields {
    placeholderImage = "placeholderImage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.placeholderImage]: string;
    };
}

type Props = OwnProps & HasProduct;

export interface ProductDetailsApplicationsStyles {
    block?: BlockProps;
    anchorHeading?: AnchorHeadingStyles;
    gridContainer?: GridContainerProps;
    gridItem?: GridItemProps;
    categoryImage?: LazyImageProps;
    categoryLink?: LinkPresentationProps;
    placeholderImg?: ImgProps;
}

export const productDetailsApplicationsStyles: ProductDetailsApplicationsStyles = {
    block: {
        variant: "separated",
        variantSide: "bottom",
    },
    anchorHeading: {
        title: {
            variant: "h4",
            forwardAs: "h2",
        },
    },
    gridContainer: {
        gap: 0,
    },
    gridItem: {
        width: [12, 6, 4, 3, 3],
        css: css`
            flex-direction: row;
            align-items: center;
            justify-content: flex-start;
            gap: ${pxToRem(10)};
        `,
    },
    categoryImage: {
        css: css`
            ${x => x.theme.lazyImage.defaultProps?.css}
            img {
                width: ${pxToRem(70)};
                height: ${pxToRem(70)};
            }
        `,
    },
    categoryLink: {
        css: css`
            ${linkAltStyles()}
            font-size: ${x => x.theme.design.typography.size.small};
            font-weight: ${x => x.theme.design.typography.weight.base};
            line-height: ${x => x.theme.design.typography.lineHeight.semi};
        `,
    },
    placeholderImg: {
        css: css`
            width: ${pxToRem(70)};
            height: ${pxToRem(70)};
        `,
    },
};

const styles = productDetailsApplicationsStyles;

const ProductDetailsApplications: React.FC<Props> = ({ fields }) => {
    const { product } = useContext(ProductContext);

    let applicationsCategories: ApplicationsCategoriesModel[] = [];

    try {
        applicationsCategories = JSON.parse(product.properties.applicationsCategories);
    } catch (error) {
        return null;
    }

    if (!applicationsCategories?.length) {
        return null;
    }

    return (
        <Block {...styles.block}>
            <StyledWrapper>
                <AnchorHeading
                    title={translate("Applications")}
                    anchor={translate("Applications")}
                    extendedStyles={styles.anchorHeading}
                />
                <GridContainer {...styles.gridContainer}>
                    {applicationsCategories.map((app, index) => (
                        <GridItem {...styles.gridItem} key={index}>
                            {app.icon ? (
                                <Link href={app.uri}>
                                    <LazyImage
                                        src={app.icon}
                                        altText={app.shortDescription}
                                        {...styles.categoryImage}
                                    />
                                </Link>
                            ) : (
                                <Img
                                    {...styles.placeholderImg}
                                    alt={app.shortDescription}
                                    src={fields.placeholderImage}
                                />
                            )}

                            <Link {...styles.categoryLink} href={app.uri}>
                                <Typography>{app.shortDescription}</Typography>
                            </Link>
                        </GridItem>
                    ))}
                </GridContainer>
            </StyledWrapper>
        </Block>
    );
};

const widgetModule: WidgetModule = {
    component: ProductDetailsApplications,
    definition: {
        displayName: "Applications",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.placeholderImage,
                displayName: "Placeholder Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
